import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ClipboardCopy, Flame, Instagram, Snowflake, Swords } from "lucide-react";
import { toast } from "sonner";

const IG_URL = "https://instagram.com/grill_enforcers";

const LACES = [
    { id: "red", name: "PENALTY RED", hex: "#DC2626", index: 2 },
    { id: "white", name: "HOME ICE WHITE", hex: "#F8FAFC", index: 0 },
    { id: "black", name: "BLACKOUT", hex: "#3F3F46", index: 3 },
    { id: "blue", name: "POWER PLAY BLUE", hex: "#2563EB", index: 1 },
];

const SPECS = [
    { icon: Swords, label: "Serrated stainless blade, laser-engraved GRILL ENFORCERS" },
    { icon: Flame, label: "Bottle-opener slot built into the blade neck" },
    { icon: Snowflake, label: "Handle forged from a game-used hockey stick shaft" },
    { icon: Check, label: "Authentic pro skate-lace hang loop — pick your colour" },
];

const fadeUp = {
    hidden: { opacity: 0, y: 44 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] },
    }),
};

export default function ShopGallery() {
    const [lace, setLace] = useState(LACES[0]);

    const copyOrder = async () => {
        const text = `Hey @grill_enforcers! I'd like to order The Enforcer spatula ($40) with the ${lace.name} lace.`;
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Order message copied — paste it in our Instagram DM");
        } catch (e) {
            toast.error("Copy failed — long-press the DM button instead");
        }
    };

    return (
        <section
            id="shop"
            data-testid="shop-gallery-section"
            className="relative bg-[#0A0A0C] px-5 py-20 sm:px-10 sm:py-28 lg:px-16"
        >
            <div className="noise-layer" />
            <div className="mx-auto grid max-w-[1380px] gap-14 lg:grid-cols-2 lg:gap-20">
                {/* product stage */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-80px" }}
                    className="relative"
                >
                    <div
                        data-testid="product-stage"
                        className="relative overflow-hidden border border-[#2A2A36] bg-[#0B0B0E]"
                    >
                        <img
                            src="/assets/asset3.jpg"
                            alt="The Enforcer spatula lineup in all four skate lace colours"
                            data-testid="product-image-frame"
                            className="block h-auto w-full"
                        />
                        {LACES.map((l) => (
                            <div
                                key={l.id}
                                className="pointer-events-none absolute inset-y-0 w-1/4 bg-[#060608] transition-opacity duration-500"
                                style={{
                                    left: `${l.index * 25}%`,
                                    opacity: lace.id === l.id ? 0 : 0.55,
                                }}
                            />
                        ))}
                        <motion.div
                            animate={{ left: `${lace.index * 25}%` }}
                            transition={{ duration: 0.5, ease: [0.7, 0, 0.2, 1] }}
                            data-testid="lace-highlight-frame"
                            className="pointer-events-none absolute inset-y-0 w-1/4 border-2"
                            style={{
                                borderColor: lace.hex,
                                boxShadow: `inset 0 0 60px ${lace.hex}33, 0 0 44px ${lace.hex}55`,
                            }}
                        />
                        <div
                            className="pointer-events-none absolute inset-0"
                            style={{
                                background:
                                    "radial-gradient(ellipse at 50% 30%, rgba(255,255,255,0.10), transparent 55%), linear-gradient(to top, rgba(6,6,8,0.85), transparent 40%)",
                            }}
                        />
                        <div
                            className="pointer-events-none absolute inset-0 transition-shadow duration-700"
                            style={{ boxShadow: `inset 0 0 120px ${lace.hex}22` }}
                        />
                        <div className="absolute left-4 top-4 border border-[#2A2A36] bg-[#0B0B0E]/90 px-3 py-2">
                            <span className="font-jbmono text-[10px] uppercase tracking-widest text-[#A1A1B2]">LACE: </span>
                            <span
                                data-testid="selected-lace-label"
                                className="font-jbmono text-[10px] uppercase tracking-widest"
                                style={{ color: lace.id === "white" ? "#F8FAFC" : lace.hex }}
                            >
                                {lace.name}
                            </span>
                        </div>
                        <div className="absolute right-4 top-4 rotate-3 border-2 border-[#FF3B30] bg-[#0B0B0E]/90 px-3 py-1.5">
                            <span
                                data-testid="product-price-display"
                                className="font-cond text-2xl font-extrabold text-[#FF3B30]"
                            >
                                $40
                            </span>
                        </div>
                        <div className="absolute bottom-4 left-4 font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#A1A1B2]">
                            The Enforcer — 001
                        </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                        <span className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-[#A1A1B2]/60">
                            FIG. 01 — FULL LINEUP
                        </span>
                        <span className="font-jbmono text-[9px] uppercase tracking-[0.2em] text-[#A1A1B2]/60">
                            4 LACES / 1 SPATULA
                        </span>
                    </div>
                </motion.div>

                {/* details */}
                <div className="flex flex-col justify-center">
                    <motion.p
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        className="font-jbmono mb-6 text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] sm:text-xs"
                    >
                        01 — THE LINEUP
                    </motion.p>
                    <motion.h2
                        variants={fadeUp}
                        custom={1}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        className="font-cond text-5xl font-extrabold uppercase leading-[0.9] tracking-tight text-[#F2F2F7] sm:text-6xl lg:text-7xl"
                    >
                        One spatula.
                        <br />
                        <span className="text-outline-ember">Four laces.</span>
                    </motion.h2>
                    <motion.p
                        variants={fadeUp}
                        custom={2}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        className="mt-6 max-w-md text-sm leading-relaxed text-[#A1A1B2] sm:text-base"
                    >
                        Every Enforcer starts life as a hockey stick that gave everything on
                        the ice. We cut it down, bolt on a heavy-gauge serrated blade, and
                        finish it with a real skate-lace loop in your team colour. Then it
                        reports for BBQ duty.
                    </motion.p>

                    <motion.div
                        variants={fadeUp}
                        custom={3}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        className="mt-8"
                    >
                        <span className="font-jbmono text-[10px] uppercase tracking-[0.3em] text-[#A1A1B2]">
                            PICK YOUR LACE
                        </span>
                        <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                            {LACES.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`lace-color-${l.id}-button`}
                                    onClick={() => setLace(l)}
                                    className={`group flex flex-col items-center gap-2 border px-3 py-4 transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 ${
                                        lace.id === l.id
                                            ? "border-[#FF3B30] bg-[#FF3B30]/10"
                                            : "border-[#2A2A36] bg-[#121216] hover:border-[#A1A1B2]"
                                    }`}
                                >
                                    <span
                                        className="h-5 w-5 rounded-full border border-white/20"
                                        style={{ backgroundColor: l.hex }}
                                    />
                                    <span className="font-jbmono text-[9px] uppercase leading-relaxed tracking-wider text-[#F2F2F7]">
                                        {l.name}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </motion.div>

                    <motion.ul
                        variants={fadeUp}
                        custom={4}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        className="mt-8 space-y-3 border-t border-[#2A2A36] pt-6"
                    >
                        {SPECS.map((s) => (
                            <li key={s.label} className="flex items-start gap-3">
                                <s.icon
                                    className="mt-0.5 h-4 w-4 shrink-0 text-[#FF3B30]"
                                    strokeWidth={2.25}
                                />
                                <span className="text-sm text-[#A1A1B2]">{s.label}</span>
                            </li>
                        ))}
                    </motion.ul>

                    <motion.div
                        variants={fadeUp}
                        custom={5}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        className="mt-9 flex flex-col gap-3 sm:flex-row"
                    >
                        <a
                            href={IG_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="order-instagram-dm-button"
                            className="group flex flex-1 items-center justify-center gap-3 border-2 border-[#FF3B30] bg-[#FF3B30] px-6 py-3.5 font-cond text-xl font-bold uppercase tracking-wide text-[#0A0A0C] transition-[transform,background-color,color] duration-150 hover:-translate-y-0.5 hover:bg-transparent hover:text-[#FF3B30]"
                        >
                            <Instagram className="h-4 w-4" strokeWidth={2.5} />
                            DM @GRILL_ENFORCERS TO ORDER
                        </a>
                        <button
                            onClick={copyOrder}
                            data-testid="copy-order-message-button"
                            className="flex items-center justify-center gap-3 border-2 border-[#2A2A36] bg-transparent px-6 py-3.5 font-cond text-xl font-bold uppercase tracking-wide text-[#A1A1B2] transition-[border-color,color,transform] duration-150 hover:-translate-y-0.5 hover:border-[#F2F2F7] hover:text-[#F2F2F7]"
                        >
                            <ClipboardCopy className="h-4 w-4" strokeWidth={2.5} />
                            COPY ORDER MESSAGE
                        </button>
                    </motion.div>
                    <motion.p
                        variants={fadeUp}
                        custom={6}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true }}
                        className="mt-4 font-jbmono text-[11px] uppercase tracking-[0.2em] text-[#A1A1B2]/60"
                    >
                        $40 each — tell us your lace colour in the DM
                    </motion.p>
                </div>
            </div>
        </section>
    );
}
